import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUser,
  faClock,
  faBullseye,
  faMapMarkedAlt,
  faClipboardList,
  faShieldAlt,
  faNetworkWired,
  faStickyNote,
  faFileAlt,
  faCalendarAlt,
  faCheckCircle,
  faExclamationTriangle,
  faBan,
  faQuestionCircle,
  faAdjust,
  faExternalLinkAlt,
  faCamera,
  faPaperclip,
  faExclamationCircle
} from '@fortawesome/free-solid-svg-icons';
import riskScoringService from '../../services/riskScoringService';
import SIEMCorrelationSection from './SIEMCorrelationSection';
import { faRobot } from '@fortawesome/free-solid-svg-icons';

const RESULT_CONFIG = {
  successful: { bg: 'bg-green-100 dark:bg-green-800', text: 'text-green-800 dark:text-green-100', icon: faCheckCircle },
  detected: { bg: 'bg-yellow-100 dark:bg-yellow-800', text: 'text-yellow-800 dark:text-yellow-100', icon: faExclamationTriangle },
  blocked: { bg: 'bg-red-100 dark:bg-red-800', text: 'text-red-800 dark:text-red-100', icon: faBan },
  failed: { bg: 'bg-gray-100 dark:bg-gray-600', text: 'text-gray-800 dark:text-gray-100', icon: faBan },
  partial: { bg: 'bg-blue-100 dark:bg-blue-800', text: 'text-blue-800 dark:text-blue-100', icon: faAdjust },
  unknown: { bg: 'bg-gray-100 dark:bg-gray-600', text: 'text-gray-600 dark:text-gray-200', icon: faQuestionCircle }
};

const RISK_COLORS = {
  Critical: { bg: 'bg-red-100 dark:bg-red-900/50', text: 'text-red-700 dark:text-red-200', border: 'border-red-300 dark:border-red-700' },
  High: { bg: 'bg-orange-100 dark:bg-orange-900/50', text: 'text-orange-700 dark:text-orange-200', border: 'border-orange-300 dark:border-orange-700' },
  Medium: { bg: 'bg-yellow-100 dark:bg-yellow-900/50', text: 'text-yellow-700 dark:text-yellow-200', border: 'border-yellow-300 dark:border-yellow-700' },
  Low: { bg: 'bg-green-100 dark:bg-green-900/50', text: 'text-green-700 dark:text-green-200', border: 'border-green-300 dark:border-green-700' }
};

const ActionDetailView = ({ action, isDarkMode, t, formatDate, onRecheck }) => {
  if (!action) return null;

  // Normalize action fields from various data shapes
  const title = action.title || action.name || action.action || t('actions.actionTitle');
  const description = action.description || '';
  const result = action.result || action.status || 'unknown';
  const resultCfg = RESULT_CONFIG[result] || RESULT_CONFIG.unknown;
  const operator = action.operator || action.executedBy || '';
  const notes = action.notes || '';
  const evidence = action.evidence || [];

  // MITRE technique
  const mitre = action.mitreAttackTechnique || action.mitre_technique || action.technique || null;
  const techniqueId = mitre?.id || mitre?.technique_id || action.techniqueId || '';
  const techniqueName = mitre?.name || action.techniqueName || '';
  const tactic = mitre?.tactic || mitre?.tactics?.[0] || action.tactic || '';

  // Timestamps
  const startTime = action.startTimestamp || action.timestamp || action.startTime || action.start_time || action.createdAt;
  const endTime = action.endTimestamp || action.endTime || action.end_time;

  // Duration
  const getDuration = () => {
    if (!startTime || !endTime) return null;
    const diffMs = new Date(endTime) - new Date(startTime);
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const mins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  // Targets and sources
  const targets = (action.targets || []).map(t => typeof t === 'object' ? t : { _id: t, value: t });
  const sources = (action.sources || []).map(s => typeof s === 'object' ? s : { _id: s, value: s });

  // Risk assessment
  const riskAssessment = (() => {
    try {
      return riskScoringService.calculateActionRisk(action, targets);
    } catch {
      return { score: 0, level: 'Low', factors: [], recommendations: [] };
    }
  })();
  const riskColors = RISK_COLORS[riskAssessment.level] || RISK_COLORS.Low;

  const duration = getDuration();

  return (
    <div className="p-6 space-y-5">
      {/* Header info: result badge, risk badge, technique */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{title}</h2>
          {techniqueId && (
            <p className="text-sm text-purple-500 mt-1">
              <FontAwesomeIcon icon={faMapMarkedAlt} className="mr-1" />
              {techniqueId}{techniqueName ? ` - ${techniqueName}` : ''}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {riskAssessment.score > 0 && (
            <span className={`px-2 py-1 rounded-full text-xs font-medium border ${riskColors.bg} ${riskColors.text} ${riskColors.border}`}>
              <FontAwesomeIcon icon={faExclamationCircle} className="mr-1" />
              Risk: {riskAssessment.score}/10
            </span>
          )}
          <span className={`px-3 py-1 rounded-full text-sm font-medium inline-flex items-center gap-1 ${resultCfg.bg} ${resultCfg.text}`}>
            <FontAwesomeIcon icon={resultCfg.icon} className="text-xs" />
            {t(`actions.${result}`) !== `actions.${result}` ? t(`actions.${result}`) : result.charAt(0).toUpperCase() + result.slice(1)}
          </span>
          {action.siemCorrelation?.summary?.autoUpdatedResult && (
            <span className="px-2 py-0.5 rounded-full text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300">
              <FontAwesomeIcon icon={faRobot} className="mr-1" />
              {t('siemCorrelation.autoCorrelated') !== 'siemCorrelation.autoCorrelated' ? t('siemCorrelation.autoCorrelated') : 'Auto-correlated'}
            </span>
          )}
        </div>
      </div>

      {/* Meta row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {operator && (
          <div>
            <div className={`text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              <FontAwesomeIcon icon={faUser} className="mr-1" />
              {t('actions.operator')}
            </div>
            <div className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{operator}</div>
          </div>
        )}
        {startTime && (
          <div>
            <div className={`text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              <FontAwesomeIcon icon={faCalendarAlt} className="mr-1" />
              {t('actions.startTime')}
            </div>
            <div className={`text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
              {formatDate(startTime, { hour: '2-digit', minute: '2-digit' })}
            </div>
          </div>
        )}
        {endTime && (
          <div>
            <div className={`text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              <FontAwesomeIcon icon={faClock} className="mr-1" />
              {t('actions.endTime')}
            </div>
            <div className={`text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
              {formatDate(endTime, { hour: '2-digit', minute: '2-digit' })}
            </div>
          </div>
        )}
        {tactic && (
          <div>
            <div className={`text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              <FontAwesomeIcon icon={faShieldAlt} className="mr-1" />
              {t('actions.tactic')}
            </div>
            <div className={`text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>{tactic}</div>
          </div>
        )}
      </div>

      {/* Duration */}
      {duration && (
        <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm ${isDarkMode ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-50 text-blue-700'}`}>
          <FontAwesomeIcon icon={faClock} className="mr-1" />
          {t('actions.duration') !== 'actions.duration' ? t('actions.duration') : 'Duration'}: {duration}
        </div>
      )}

      {/* Description */}
      {description && (
        <div>
          <h4 className={`text-sm font-semibold mb-2 flex items-center ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            <FontAwesomeIcon icon={faClipboardList} className="mr-2 text-blue-500" />
            {t('common.description')}
          </h4>
          <p className={`text-sm leading-relaxed whitespace-pre-line ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{description}</p>
        </div>
      )}

      {/* Risk Assessment */}
      {riskAssessment.score > 0 && (
        <div>
          <h4 className={`text-sm font-semibold mb-2 flex items-center ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            <FontAwesomeIcon icon={faExclamationCircle} className="mr-2 text-orange-500" />
            {t('actions.riskAssessment') !== 'actions.riskAssessment' ? t('actions.riskAssessment') : 'Risk Assessment'}
          </h4>
          <div className={`p-3 rounded-lg border ${riskColors.bg} ${riskColors.border}`}>
            <div className="flex justify-between items-center mb-2">
              <span className={`font-medium text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                {t('actions.riskScore') !== 'actions.riskScore' ? t('actions.riskScore') : 'Risk Score'}
              </span>
              <span className={`text-lg font-bold ${riskColors.text}`}>
                {riskAssessment.score}/10 ({riskAssessment.level})
              </span>
            </div>
            {riskAssessment.factors.length > 0 && (
              <div className={`text-xs space-y-0.5 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                {riskAssessment.factors.map((factor, i) => (
                  <div key={i}>- {factor.factor}: {factor.detail}</div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* SIEM Correlation */}
      {action.siemCorrelation?.status && action.siemCorrelation.status !== 'none' && (
        <SIEMCorrelationSection action={action} isDarkMode={isDarkMode} onRecheck={onRecheck} />
      )}

      {/* Targets */}
      {targets.length > 0 && (
        <div>
          <h4 className={`text-sm font-semibold mb-2 flex items-center ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            <FontAwesomeIcon icon={faBullseye} className="mr-2 text-orange-500" />
            {t('common.targets')}
          </h4>
          <div className="flex flex-wrap gap-2">
            {targets.map((target, i) => (
              <span
                key={target._id || i}
                className={`px-3 py-1 rounded-full text-sm ${
                  isDarkMode ? 'bg-gray-700 text-gray-200' : 'bg-blue-50 text-blue-800'
                }`}
              >
                {target.value || target.hostname || target._id}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Sources */}
      {sources.length > 0 && (
        <div>
          <h4 className={`text-sm font-semibold mb-2 flex items-center ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            <FontAwesomeIcon icon={faNetworkWired} className="mr-2 text-teal-500" />
            {t('actions.sources') !== 'actions.sources' ? t('actions.sources') : 'Sources'}
          </h4>
          <div className="flex flex-wrap gap-2">
            {sources.map((source, i) => (
              <span
                key={source._id || i}
                className={`px-3 py-1 rounded-full text-sm ${
                  isDarkMode ? 'bg-gray-700 text-gray-200' : 'bg-teal-50 text-teal-800'
                }`}
              >
                {source.value || source.hostname || source._id}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Notes */}
      {notes && (
        <div>
          <h4 className={`text-sm font-semibold mb-2 flex items-center ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            <FontAwesomeIcon icon={faStickyNote} className="mr-2 text-yellow-500" />
            {t('actions.notes')}
          </h4>
          <pre className={`text-sm whitespace-pre-wrap p-3 rounded-lg ${isDarkMode ? 'bg-gray-900 text-gray-300' : 'bg-gray-50 text-gray-700'}`}>
            {notes}
          </pre>
        </div>
      )}

      {/* Evidence / Attachments */}
      {evidence.length > 0 && (
        <div>
          <h4 className={`text-sm font-semibold mb-2 flex items-center ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            <FontAwesomeIcon icon={faFileAlt} className="mr-2 text-indigo-500" />
            {t('actions.evidence')} ({evidence.length})
          </h4>
          <div className="space-y-2">
            {evidence.map((ev, i) => (
              <div key={i}>
                {(typeof ev === 'object' && ev.type === 'screenshot' && ev.data) ? (
                  <div>
                    <img
                      src={ev.data}
                      alt={ev.caption || `Evidence ${i + 1}`}
                      className="max-w-full h-auto rounded border border-gray-300 dark:border-gray-600 cursor-pointer hover:opacity-90"
                      onClick={() => window.open(ev.data, '_blank')}
                    />
                    {ev.caption && (
                      <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{ev.caption}</p>
                    )}
                  </div>
                ) : (typeof ev === 'string' && ev.startsWith('data:image')) ? (
                  <div className="flex items-center space-x-2">
                    <img
                      src={ev}
                      alt={`Evidence ${i + 1}`}
                      className="h-20 w-20 object-cover rounded border border-gray-300 dark:border-gray-600 cursor-pointer hover:border-blue-500"
                      onClick={() => window.open(ev, '_blank')}
                    />
                    <span className={`text-sm flex items-center ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      <FontAwesomeIcon icon={faCamera} className="mr-1" />
                      Screenshot {i + 1}
                    </span>
                  </div>
                ) : (typeof ev === 'string') ? (
                  <a
                    href={ev}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-500 hover:underline break-all flex items-center"
                  >
                    <FontAwesomeIcon icon={faPaperclip} className="mr-1" />
                    {ev}
                    <FontAwesomeIcon icon={faExternalLinkAlt} className="ml-1 text-xs" />
                  </a>
                ) : (
                  <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    <FontAwesomeIcon icon={faPaperclip} className="mr-1" />
                    {ev.url || ev.filename || JSON.stringify(ev)}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ActionDetailView;
